// What a QUOTE requires, as opposed to a sales order.
//
// Kevin, 2026-08-27: "on the quote we only need the customer requirements tab and in the
// firetube tab we only need internals" — and, asked which sections, since nothing in that
// tab is headed "internals": Main Burner + Pilot + Ignitor. Then, having seen it: "lets
// bring back what we took out of the sales order and just say its (optional)."
//
// So the first cut HID everything else, and this does not. A quote now shows the same form a
// sales order does; the sections a quote does not need are headed "(optional)" and are not
// counted as missing. Better than hiding, because a quote that DOES know its flange or its
// hood can still say so — hiding the fields threw that away and forced it to be re-entered
// on the order later.
//
// Sales orders are untouched: every section is required there, as it always was. A field
// that no launch requires is not this module's business -- that is the product profile's
// optionalQuestionCodes (Required Heat Duty and Fuel Pressure went there on 2026-09-01).
//
// A quote is a price, not a build: the housing, flange, inlet, hood and manway detail is
// usually work asked for months before anyone can answer it.
//
// ONE SOURCE OF TRUTH. The UI (Group headings) and validation (collectRequiredVisible) both
// read this, which is what keeps "marked optional" and "not required" from drifting apart.
// A section labelled optional whose fields still blocked Save & Submit would be worse than
// no label at all.
import { getLaunchContext } from "./launchContext";

/** Tab codes whose groups a quote REQUIRES in full. INPUTS_TAB is "Customer Requirements". */
const QUOTE_REQUIRED_TAB_CODES: ReadonlySet<string> = new Set(["INPUTS_TAB"]);

/** Within a tab not listed above, the only groups a quote requires.
 *
 *  These are group CODES, not ids or headings: the code is the stable identifier — headings
 *  are editable in NetSuite and ids are meaningless to read. Worth knowing if you go looking:
 *  MAIN_BURNER's own questions carry a stale NetSuite path reading "Center Firetube / Main
 *  Internals", which is why "internals" named it, but the heading on screen says Main Burner. */
const QUOTE_REQUIRED_GROUP_CODES: ReadonlySet<string> = new Set([
  "MAIN_BURNER",
  "PILOT",
  "IGNITOR",
]);

/** Is this a quote/estimate launch?
 *
 *  originType is normalised by launchContext: "Quo" for estimate/quote, "SO" for a sales
 *  order, "Opp" for an opportunity. Anything else — including a launch with no type at all,
 *  which is what a direct visit looks like — is NOT a quote, so the full, fully-required
 *  configurator is the default and this relaxation is something a launch has to ask for.
 *  Getting that backwards would quietly stop requiring most of a real build sheet. */
export function isQuoteLaunch(): boolean {
  return getLaunchContext().originType === "Quo";
}

/** Is this group optional on THIS launch? False for every group on a sales order.
 *
 *  Optional means two things together, and they must stay together: the heading says
 *  "(optional)", and validation does not count its fields as missing. */
export function isOptionalGroupForLaunch(
  tabCode: string | null | undefined,
  groupCode: string | null | undefined
): boolean {
  if (!isQuoteLaunch()) return false;
  if (QUOTE_REQUIRED_TAB_CODES.has(String(tabCode ?? ""))) return false;
  return !QUOTE_REQUIRED_GROUP_CODES.has(String(groupCode ?? ""));
}
